/**
 * OptionsDialog — Dialog shell wrapping OptionsEngine.
 *
 * A centred modal that presents config-driven options to the operator,
 * collects their selections, and returns them via onContinue.
 *
 * Props:
 *   open             — boolean: controls dialog visibility
 *   onClose          — () => void: called on X button click
 *   config           — { title, subtitle?, items[] }: dialog content config
 *   supportData      — optional object for multiselect dynamic data
 *   onContinue       — (values) => void: called with form values on primary action
 *   onBack           — optional () => void: if provided, shows Back button (left side)
 *   onClear          — optional () => void: if provided, shows Clear button (left side, resets engine)
 *   continueLabel    — optional string (default "Continue")
 *   backLabel        — optional string (default "Back")
 *   clearLabel       — optional string (default "Clear")
 *   continueDisabled — optional boolean: external disable for Continue button
 */
import {useRef, useState} from "react";
import {Dialog, DialogContent, Grid, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import {OptionsEngine} from "../OptionsEngine/OptionsEngine";
import {StyledButtonContained, StyledButtonOutlined} from "../../utils/uiUtils";

export const OptionsDialog = ({
                                  open,
                                  onClose,
                                  config = {},
                                  supportData = {},
                                  onContinue,
                                  onBack,
                                  onClear,
                                  continueLabel = "Continue",
                                  backLabel = "Back",
                                  clearLabel = "Clear",
                                  continueDisabled = false,
                                  computeCanProceed,
                              }) => {
    const engineRef = useRef();
    const [isValid, setIsValid] = useState(true);
    const [canProceed, setCanProceed] = useState(!computeCanProceed);

    const items = config.items || [];
    const hasItems = items.length > 0;

    const handleContinue = () => {
        if (hasItems) {
            engineRef.current?.submit();
        } else {
            onContinue?.({});
        }
    };

    const handleClear = () => {
        engineRef.current?.reset();
        onClear?.();
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            PaperProps={{sx: {borderRadius: "12px", backgroundColor: "#3A3E3F"}}}
        >
            <DialogContent sx={{p: 3, display: "flex", flexDirection: "column", maxHeight: "80vh"}}>
                {/* Header */}
                <Grid container justifyContent="space-between" alignItems="flex-start" sx={{mb: 2, flexShrink: 0}}>
                    <Grid item>
                        {config.title && (
                            <Typography sx={{color: "#FFC540", fontWeight: 600, fontSize: "18px", fontFamily: "Poppins, sans-serif"}}>
                                {config.title}
                            </Typography>
                        )}
                        {config.subtitle && (
                            <Typography sx={{color: "rgba(255,255,255,0.5)", fontSize: "12px", fontFamily: "Poppins, sans-serif", mt: 0.5}}>
                                {config.subtitle}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item>
                        <CloseIcon
                            onClick={onClose}
                            sx={{color: "#FFFFFF", cursor: "pointer", fontSize: "20px"}}
                        />
                    </Grid>
                </Grid>

                {/* Content — scrollable */}
                <Box sx={{flexGrow: 1, overflowY: "auto", mb: 2}}>
                    {hasItems ? (
                        <OptionsEngine
                            ref={engineRef}
                            config={items}
                            supportData={supportData}
                            onSubmit={onContinue}
                            setIsValid={setIsValid}
                            computeCanProceed={computeCanProceed}
                            setCanProceed={setCanProceed}
                        />
                    ) : (
                        <Typography sx={{color: "rgba(255,255,255,0.5)", fontSize: "14px", fontFamily: "Poppins, sans-serif", py: 3}}>
                            No customisation options available.
                        </Typography>
                    )}
                </Box>

                {/* Footer */}
                <Grid container justifyContent="space-between" alignItems="center" sx={{flexShrink: 0}}>
                    <Grid item>
                        {onBack && (
                            <StyledButtonOutlined
                                variant="outlined"
                                onClick={onBack}
                                sx={{width: "160px", height: "50px"}}
                            >
                                {backLabel}
                            </StyledButtonOutlined>
                        )}
                        {!onBack && hasItems && (
                            <StyledButtonOutlined
                                variant="outlined"
                                onClick={handleClear}
                                sx={{width: "160px", height: "50px", borderColor: "#ffffff", color: "#ffffff"}}
                            >
                                {clearLabel}
                            </StyledButtonOutlined>
                        )}
                    </Grid>
                    <Grid item>
                        <StyledButtonContained
                            variant="contained"
                            onClick={handleContinue}
                            disabled={hasItems && (!isValid || !canProceed || continueDisabled)}
                            sx={{width: "160px", height: "50px", borderRadius: "30px"}}
                        >
                            {continueLabel}
                        </StyledButtonContained>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};
